import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Copy, Code2, ShieldCheck, Zap, Webhook } from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [
  { id: "quickstart", label: "Quickstart" },
  { id: "auth", label: "Authentication" },
  { id: "widget", label: "Drop-in Widget" },
  { id: "endpoints", label: "Endpoints" },
  { id: "webhooks", label: "Webhooks" },
];

const ENDPOINTS = [
  {
    method: "POST",
    path: "/api/partner/v1/users/lookup",
    scope: "verify",
    description: "Look up an EthicCred passport by email, phone, or EthicCred ID.",
    body: `{\n  "email": "jane@example.com"\n}`,
    response: `{\n  "found": true,\n  "user": {\n    "ethiccred_id": "EC-7F4K-9LX2",\n    "display_name": "Jane Doe",\n    "trust_score": 92,\n    "tier": "Trusted"\n  }\n}`,
  },
  {
    method: "POST",
    path: "/api/partner/v1/users",
    scope: "create",
    description: "Create a new EthicCred passport from a partner session (name + email).",
    body: `{\n  "full_name": "Jane Doe",\n  "email": "jane@example.com",\n  "partner_user_ref": "coyoin-user-9211"\n}`,
    response: `{\n  "created": true,\n  "ethiccred_id": "EC-7F4K-9LX2",\n  "magic_setup_url": "/setup-account?token=..."\n}`,
  },
  {
    method: "POST",
    path: "/api/partner/v1/links",
    scope: "verify",
    description: "Link an existing EthicCred ID to one of your users for persistent display.",
    body: `{\n  "partner_user_ref": "coyoin-user-9211",\n  "ethiccred_id": "EC-7F4K-9LX2"\n}`,
    response: `{ "ok": true, "link_id": "..." }`,
  },
  {
    method: "POST",
    path: "/api/partner/v1/endorsements",
    scope: "endorse",
    description: "Submit an endorsement that lands in the user's EthicCred inbox for Accept/Reject.",
    body: `{\n  "ethiccred_id": "EC-7F4K-9LX2",\n  "role": "Freelance Developer",\n  "company": "CoyoIN",\n  "start_date": "2025-01-01",\n  "end_date": "2025-06-30",\n  "rating": 5,\n  "feedback": "Delivered on time, excellent communication.",\n  "employer_name": "Bob Hirer",\n  "employer_email": "bob@coyoin.com"\n}`,
    response: `{ "ok": true, "endorsement_id": "...", "status": "pending" }`,
  },
  {
    method: "GET",
    path: "/api/partner/v1/users/{id}/public",
    scope: "verify",
    description: "Fetch the public passport summary for a known EthicCred ID.",
    body: null,
    response: `{\n  "ethiccred_id": "EC-7F4K-9LX2",\n  "display_name": "Jane Doe",\n  "trust_score": 92,\n  "tier": "Trusted"\n}`,
  },
];

const CURL_SIGN = `# Node — compute the HMAC and call lookup
const crypto = require("crypto");
const body = JSON.stringify({ email: "jane@example.com" });
const ts   = String(Math.floor(Date.now() / 1000));
const sig  = crypto.createHmac("sha256", CLIENT_SECRET)
                   .update(ts + "\\n" + body).digest("hex");
await fetch("https://ethiccred.com/api/partner/v1/users/lookup", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-EC-Client-Id": CLIENT_ID,
    "X-EC-Timestamp": ts,
    "X-EC-Signature": sig,
  },
  body,
});`;

const WIDGET_SNIPPET = `<!-- EthicCred drop-in widget -->
<div id="ethiccred-widget"
     data-partner-id="YOUR_CLIENT_ID"
     data-user-email="\${user.email}"
     data-user-ref="\${user.id}"
     data-accent="#059669"></div>
<script src="https://ethiccred.com/widget.js" async></script>`;

export default function Developers() {
  const [active, setActive] = useState("quickstart");
  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied"); };
  return (
    <div className="bg-ec-bg text-ec-ink min-h-screen" data-testid="developers-page">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-16 grid grid-cols-12 gap-10">
        {/* Sidebar */}
        <aside className="hidden lg:block col-span-3 sticky top-24 self-start">
          <div className="eyebrow mb-3">Developer Docs</div>
          <ul className="space-y-1">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} onClick={() => setActive(s.id)} className={`block px-3 py-2 text-sm rounded-sm ${active === s.id ? "bg-ec-ink text-white" : "text-ec-ink-soft hover:bg-ec-mute"}`}>{s.label}</a>
              </li>
            ))}
          </ul>
          <div className="mt-6 p-4 border border-ec-line bg-ec-soft">
            <div className="eyebrow text-ec-emerald">Need a key?</div>
            <p className="text-xs text-ec-ink-soft mt-2">Partner keys are provisioned manually in Phase 1.</p>
            <a href="/demo?audience=partner" className="mt-3 inline-flex items-center gap-1 text-xs font-bold underline text-ec-ink">Request access →</a>
          </div>
        </aside>

        {/* Main */}
        <main className="col-span-12 lg:col-span-9 space-y-16">
          <div>
            <div className="inline-flex items-center gap-2 border border-ec-line bg-white px-3 py-1 text-xs eyebrow"><Code2 className="w-3.5 h-3.5 text-ec-emerald" /> Partner API · v1</div>
            <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter leading-[0.95] mt-5">Verify, link & endorse — <span className="text-ec-emerald">anywhere.</span></h1>
            <p className="text-lg text-ec-ink-soft mt-5 max-w-2xl">Drop EthicCred verified identity + endorsements into any site in under 10 minutes. One signed REST surface, plus a zero-config JavaScript widget.</p>
            <div className="grid sm:grid-cols-3 gap-3 mt-8">
              <Feature icon={ShieldCheck} title="HMAC-signed" sub="SHA-256 over timestamp + body, 5-minute replay window." />
              <Feature icon={Zap} title="Drop-in widget" sub="One script tag renders search, create and endorsement states." />
              <Feature icon={Webhook} title="Webhooks" sub="Phase 2 — push updates when scores or endorsements change." />
            </div>
          </div>

          <section id="quickstart">
            <h2 className="font-display text-3xl font-black">1 · Quickstart</h2>
            <ol className="mt-5 space-y-3 text-ec-ink-soft list-decimal list-inside">
              <li>Ask EthicCred to provision a partner record (name, slug, allowed domains, scopes).</li>
              <li>Receive your <code className="font-mono-plex bg-ec-mute px-1">client_id</code> and <code className="font-mono-plex bg-ec-mute px-1">client_secret</code> (shown once).</li>
              <li>Compute an HMAC signature on every request (snippet below) and start calling the API.</li>
              <li>Optionally embed the widget to render the identity card on your profile pages.</li>
            </ol>
          </section>

          <section id="auth">
            <h2 className="font-display text-3xl font-black">2 · Authentication</h2>
            <p className="text-ec-ink-soft mt-4">Every request must include three headers:</p>
            <div className="mt-4 bg-ec-ink text-zinc-100 p-5 rounded-sm font-mono-plex text-xs leading-relaxed overflow-x-auto">
              <div><span className="text-zinc-500">X-EC-Client-Id:</span> ec_live_...</div>
              <div><span className="text-zinc-500">X-EC-Timestamp:</span> &lt;unix-seconds&gt;</div>
              <div><span className="text-zinc-500">X-EC-Signature:</span> hex(HMAC-SHA256(secret, timestamp + &quot;\\n&quot; + body))</div>
            </div>
            <CodeBlock code={CURL_SIGN} onCopy={() => copy(CURL_SIGN)} />
          </section>

          <section id="widget">
            <h2 className="font-display text-3xl font-black">3 · Drop-in Widget</h2>
            <p className="text-ec-ink-soft mt-4">Paste this where you render each user's profile card. The widget auto-detects whether the user has a passport and shows the correct state.</p>
            <CodeBlock code={WIDGET_SNIPPET} onCopy={() => copy(WIDGET_SNIPPET)} />
          </section>

          <section id="endpoints">
            <h2 className="font-display text-3xl font-black">4 · Endpoints</h2>
            <div className="mt-6 space-y-6">
              {ENDPOINTS.map((e) => (
                <div key={e.path} className="border border-ec-line bg-white rounded-sm overflow-hidden" data-testid={`endpoint-${e.path.replace(/\//g, "-")}`}>
                  <div className="px-5 py-3 border-b border-ec-line flex items-center gap-3 bg-ec-soft">
                    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-bold font-mono-plex rounded-sm ${e.method === "GET" ? "bg-sky-100 text-sky-800" : "bg-ec-emerald/15 text-ec-emerald"}`}>{e.method}</span>
                    <code className="font-mono-plex text-sm">{e.path}</code>
                    <span className="ml-auto text-[10px] eyebrow text-ec-ink-soft">scope · {e.scope}</span>
                  </div>
                  <div className="p-5 grid lg:grid-cols-2 gap-5">
                    <div>
                      <p className="text-sm text-ec-ink-soft">{e.description}</p>
                      {e.body && (<>
                        <div className="eyebrow text-ec-ink-soft mt-4 mb-1">Request body</div>
                        <pre className="bg-ec-ink text-zinc-100 p-3 rounded-sm text-xs overflow-x-auto whitespace-pre font-mono-plex">{e.body}</pre>
                      </>)}
                    </div>
                    <div>
                      <div className="eyebrow text-ec-ink-soft mb-1">Response</div>
                      <pre className="bg-ec-ink text-zinc-100 p-3 rounded-sm text-xs overflow-x-auto whitespace-pre font-mono-plex">{e.response}</pre>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="webhooks">
            <h2 className="font-display text-3xl font-black">5 · Webhooks <span className="text-sm font-sans text-ec-ink-soft">(Phase 2)</span></h2>
            <p className="text-ec-ink-soft mt-4">Register a <code className="font-mono-plex bg-ec-mute px-1">webhook_url</code> on your partner record to receive signed events: <code className="font-mono-plex bg-ec-mute px-1">user.linked</code>, <code className="font-mono-plex bg-ec-mute px-1">user.created</code>, <code className="font-mono-plex bg-ec-mute px-1">endorsement.accepted</code>, <code className="font-mono-plex bg-ec-mute px-1">endorsement.rejected</code>, <code className="font-mono-plex bg-ec-mute px-1">trust_score.updated</code>.</p>
          </section>
        </main>
      </section>
      <Footer />
    </div>
  );
}

function Feature({ icon: Icon, title, sub }) {
  return (
    <div className="border border-ec-line bg-white p-4">
      <Icon className="w-5 h-5 text-ec-emerald" />
      <div className="font-display font-bold mt-2">{title}</div>
      <div className="text-xs text-ec-ink-soft mt-1 leading-relaxed">{sub}</div>
    </div>
  );
}

function CodeBlock({ code, onCopy }) {
  return (
    <div className="relative mt-4 group">
      <pre className="bg-ec-ink text-zinc-100 p-5 rounded-sm text-xs overflow-x-auto whitespace-pre font-mono-plex leading-relaxed">{code}</pre>
      <Button onClick={onCopy} variant="outline" className="absolute top-3 right-3 rounded-sm h-8 bg-white/10 border-white/20 text-white hover:bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"><Copy className="w-3 h-3 mr-1" />Copy</Button>
    </div>
  );
}
